import { create } from 'zustand';

import api from '@/services/api';
import { AppNotification } from '@/types';
import { alsListe } from '@/utils/error';

interface NotificationState {
  notifications: AppNotification[];
  unreadCount: number;
  /**
   * Ungelesene KRITISCHE Admin-Meldungen (Decision #207, 16.09.2026) — die
   * Liste, die `AdminAlarmModal` als Popup zeigt. Kommt mit jedem
   * unread-count-Poll (30 s) frisch vom Server; Quittieren = gelesen stellen.
   */
  kritischUngelesen: AppNotification[];
  loading: boolean;
  pollingInterval: ReturnType<typeof setInterval> | null;
  page: number;
  lastPage: number;
  total: number;
}

interface NotificationActions {
  fetchNotifications: (page?: number) => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  /** Kritische Admin-Meldung quittieren = gelesen stellen; sie verlaesst das Popup sofort. */
  quittieren: (id: string) => Promise<void>;
  startPolling: () => void;
  stopPolling: () => void;
  clearState: () => void;
}

type NotificationStore = NotificationState & NotificationActions;

const initialState: NotificationState = {
  notifications: [],
  unreadCount: 0,
  kritischUngelesen: [],
  loading: false,
  pollingInterval: null,
  page: 1,
  lastPage: 1,
  total: 0,
};

/** Takt des unread-count-Polls (unveraendert 30 s). */
export const UNREAD_POLL_MS = 30_000;

const tabVerdeckt = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';

/** Der visibilitychange-Hoerer des laufenden Pollings — genau einer. */
let beiSichtbarkeit: (() => void) | null = null;

const beobachtungBeenden = () => {
  if (beiSichtbarkeit) document.removeEventListener('visibilitychange', beiSichtbarkeit);
  beiSichtbarkeit = null;
};

const useNotificationStore = create<NotificationStore>((set, get) => ({
  ...initialState,

  fetchNotifications: async (page = 1) => {
    set({ loading: true });
    try {
      const response = await api.notification.getNotifications(page);
      set({
        notifications: alsListe(response.data, 'notifications.notifications'),
        unreadCount: response.unread_count,
        page: response.meta.current_page,
        lastPage: response.meta.last_page,
        total: response.meta.total,
      });
    } catch {
      // silent fail for polling
    } finally {
      set({ loading: false });
    }
  },

  fetchUnreadCount: async () => {
    try {
      const response = await api.notification.getUnreadCount();
      set({
        unreadCount: response.data.count,
        kritischUngelesen: Array.isArray(response.data.kritisch) ? response.data.kritisch : [],
      });
    } catch {
      // silent fail
    }
  },

  markAsRead: async (id: string) => {
    try {
      await api.notification.markAsRead(id);
      set((state) => ({
        notifications: state.notifications.map((n) =>
          n.id === id ? { ...n, read_at: new Date().toISOString() } : n,
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
        kritischUngelesen: state.kritischUngelesen.filter((n) => n.id !== id),
      }));
    } catch {
      // silent
    }
  },

  quittieren: async (id: string) => {
    // Optimistisch aus dem Popup nehmen — der Server bestaetigt; schlaegt er
    // fehl, bringt der naechste Poll die Meldung zurueck (bleibt bis Quittierung).
    set((state) => ({ kritischUngelesen: state.kritischUngelesen.filter((n) => n.id !== id) }));
    try {
      await api.notification.markAsRead(id);
      set((state) => ({
        notifications: state.notifications.map((n) =>
          n.id === id ? { ...n, read_at: new Date().toISOString() } : n,
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      }));
    } catch {
      // silent — der Poll stellt den Serverstand wieder her
    }
  },

  markAllAsRead: async () => {
    try {
      await api.notification.markAllAsRead();
      set((state) => ({
        notifications: state.notifications.map((n) => ({
          ...n,
          read_at: n.read_at ?? new Date().toISOString(),
        })),
        unreadCount: 0,
        kritischUngelesen: [],
      }));
    } catch {
      // silent
    }
  },

  startPolling: () => {
    const existing = get().pollingInterval;
    if (existing) clearInterval(existing);
    beobachtungBeenden();

    // Initial fetch
    get().fetchUnreadCount();
    get().fetchNotifications();

    // Verdeckter Tab: nicht fragen (Performance-Paket 1,
    // docs/tasks/ops/performance.md — nachts kamen 650–870 Abfragen/h aus
    // offenen Hintergrund-Tabs). Beim Zurueckholen fragt der
    // visibilitychange-Hoerer sofort einmal nach; der Takt bleibt 30 s.
    const interval = setInterval(() => {
      if (!tabVerdeckt()) get().fetchUnreadCount();
    }, UNREAD_POLL_MS);

    beiSichtbarkeit = () => {
      if (!tabVerdeckt()) get().fetchUnreadCount();
    };
    document.addEventListener('visibilitychange', beiSichtbarkeit);

    set({ pollingInterval: interval });
  },

  stopPolling: () => {
    const interval = get().pollingInterval;
    if (interval) clearInterval(interval);
    beobachtungBeenden();
    set({ pollingInterval: null });
  },

  clearState: () => {
    const interval = get().pollingInterval;
    if (interval) clearInterval(interval);
    beobachtungBeenden();
    set(initialState);
  },
}));

export default useNotificationStore;
